<template>
  <div class="captions-list soft full-width">
    <template v-if="loading">
      <QSpinner
        class="q-mt-smd full-width text-center"
        :size="30"
      />
    </template>
    <template v-else>
      <template v-if="captionFragments && captionFragments.length">
        <div
          v-for="(caption, key) in sortedCaptionsAndThumbnails"
          :key="key"
          class="tw:mb-4"
        >
          <div class="tw:flex tw:flex-row tw:text-denim-900">
            <div class="column">
              <span class="tw:mr-6 tw:text-base">
                {{ caption.date }}

                <StreemTooltip>
                  {{ whichTimezone }}
                </StreemTooltip>
              </span>

              <div
                v-if="!hideThumbnails"
                class="column q-pr-md justify-around"
              >
                <img
                  v-for="thumbnail in caption.thumbnails"
                  :key="thumbnail.id"
                  :alt="$t('tv_mention_transcript.thumbnail_image')"
                  class="thumbnail-img"
                  :class="{ 'q-mr-md': bigThumbnails }"
                  :src="thumbnail.url"
                  :style="{ width: bigThumbnails ? '130px' : '80px' }"
                />
              </div>
            </div>

            <div class="column col">
              <!-- eslint-disable vue/no-v-html -->
              <span
                v-for="captionFragment in caption.captionFragments"
                :key="captionFragment.id"
                class="highlightable tw:text-base self-start"
                v-html="highlight(captionFragment.text, keywords)"
              />
            </div>
          </div>
        </div>
        <div>
          <p class="text-grey text-caption q-mt-md">
            {{
              $t("tv_mention_transcript.automated_transcript_disclaimer", {
                platformName: appConfig.platformName,
              })
            }}
          </p>
        </div>
      </template>
      <template v-else>
        <p class="text-grey text-caption q-mt-md">
          {{
            $t("tv_mention_transcript.no_automated_transcript_disclaimer", {
              platformName: appConfig.platformName,
            })
          }}
        </p>
      </template>
    </template>
  </div>
</template>

<script>
import { storeToRefs } from "pinia";

import {
  addTimeZone,
  formatDate,
  getDateTimeZoneOffset,
} from "shared/helpers/date";
import { highlight } from "shared/helpers/string";
import { useGlobalStore } from "shared/stores/global";

export default {
  name: "TVMentionTranscript",
  props: {
    captionFragments: {
      type: Array,
      required: true,
    },
    thumbnails: {
      type: Array,
      required: true,
    },
    mention: {
      type: Object,
      required: true,
    },
    keywords: {
      type: Array,
      default: () => [],
    },
    bigThumbnails: {
      type: Boolean,
    },
    loading: {
      type: Boolean,
    },
    hideThumbnails: {
      type: Boolean,
    },
  },
  setup() {
    const { appConfig } = storeToRefs(useGlobalStore());

    return {
      appConfig,
    };
  },
  computed: {
    whichTimezone() {
      return getDateTimeZoneOffset(
        new Date(),
        this.mention.source.time_zone
      ) === 0
        ? this.$t("tv_mention_transcript.your_local_time")
        : this.$t("tv_mention_transcript.source_local_time", {
            location: this.mention.source.location,
          });
    },
    sortedCaptionsAndThumbnails() {
      const result = {};

      this.captionFragments.forEach((caption) => {
        const minute = formatDate(caption.timestamp * 1000, "yyyyMMddHHmm");

        if (!result[minute]) {
          result[minute] = {
            captionFragments: [],
            thumbnails: [],
            date: formatDate(
              addTimeZone(caption.timestamp, this.mention.source.time_zone),
              "h:mmaaa"
            ),
            dateToSort: caption.timestamp,
          };
        }

        result[minute].captionFragments.push(caption);
      });

      this.thumbnails.forEach((thumbnail) => {
        const minute = formatDate(thumbnail.timestamp * 1000, "yyyyMMddHHmm");

        if (!result[minute]) {
          result[minute] = {
            captions: [],
            thumbnails: [],
            date: formatDate(
              addTimeZone(thumbnail.timestamp, this.mention.source.time_zone),
              "h:mmaaa"
            ),
            dateToSort: thumbnail.timestamp,
          };
        }

        result[minute].thumbnails.push(thumbnail);
      });

      return Object.values(result)
        .filter((value) => value)
        .sort((resultA, resultB) =>
          resultA.dateToSort < resultB.dateToSort ? -1 : 1
        );
    },
  },
  methods: {
    highlight,
  },
};
</script>

<style lang="scss" scoped>
.captions-list {
  line-height: 20px;

  .caption-date {
    margin: 5px 0;
    display: inline-block;
  }

  .thumbnail-img {
    margin-top: 5px;
    margin-bottom: 5px;
    border-radius: 4px;
  }

  :deep(em) {
    background-color: rgb(252 220 89 / 30%);
    padding: 1px 2px;
    font-style: normal;
  }
}
</style>
